

























































































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Location as VueLocation } from "vue-router";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import { IBrandGateway, IGateway } from "@/models/gateways";
import { GatewayTypes, OnlineGatewayTypes, UUID } from "@/data/enums";
import {
  PaymentMethodType,
  SelectedPaymentMethod
} from "@/models/selectPaymentMethod";
import { TranslateResult } from "vue-i18n";
import {
  GatewayStoreType,
  GatewayTypesMap,
  GatewayContext
} from "@/data/enums/gateway";
import { IClient } from "@/models/clients";
import { IBrand } from "@/models/brands";
import { ICurrency } from "@/models/constants";
import _ from "@/boot/lodash";

import BankTransferGateway from "@/components/app/global/payments/brandGateways/bankTransferGateway.vue";
import CardGateway from "@/components/app/global/payments/brandGateways/cardGateway.vue";
import DirectDebitGateway from "@/components/app/global/payments/brandGateways/directDebitGateway.vue";
import ExternalStoreGateway from "@/components/app/global/payments/brandGateways/externalStoreGateway.vue";
import MobileGateway from "@/components/app/global/payments/brandGateways/mobileGateway.vue";
import OfflinePaymentGateway from "@/components/app/global/payments/brandGateways/offlinePaymentGateway.vue";
import { CreateElement } from "vue";

interface IGatewayType {
  component: any;
  isRenderless: boolean;
  label: string | TranslateResult;
  props?: { [key: string]: any };
  selector: (bg: IBrandGateway) => boolean;
  type?: GatewayTypes;
}

interface IAvailableGateway extends IGatewayType {
  brandGateway: IBrandGateway;
  instructions: string;
  id: string;
}

@Component({
  components: {
    "gateway-component": () =>
      import(
        "@/components/app/global/payments/brandGateways/gatewayComponent.vue"
      ),
    "no-stored-brand-gateways-msg": () =>
      import("@/components/app/global/payments/noStoredBrandGatewaysMsg.vue")
  },
  computed: {
    ...mapState({
      gateways(state, getters) {
        return getters[`data/listArray`]({
          storeModule: this.dataModule
        });
      }
    })
  }
})
export default class BrandGateways extends Mixins(RequestPromises, Context) {
  @Prop({ default: null })
  public value!: SelectedPaymentMethod | null;

  @Prop({ default: UUID.ORG })
  public brandId!: IBrand["id"];

  @Prop({ default: "" })
  public currencyId!: ICurrency["id"];

  @Prop({ default: "" })
  public clientId!: IClient["id"];

  @Prop({ default: "" })
  public clientName!: string;

  @Prop({ default: "" })
  public preselectedId!: IGateway["id"];

  @Prop({ default: false })
  public onlineOnly!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: false })
  public autoSelectFirst!: boolean;

  @Prop({ default: true })
  public showAddressField!: boolean;

  @Prop({ default: () => [] })
  public excludedGatewayStoreTypes!: GatewayStoreType[];

  @Prop({ default: null })
  public externalAuthReturnLocation!: VueLocation;

  @Prop({ default: false })
  public showAddManualPayment!: boolean;

  @Prop({ required: true })
  public gatewayContext!: GatewayContext;

  // Getters
  public gateways!: IBrandGateway[];
  // Data
  public dataModule = DataModules.BRAND_GATEWAYS;
  public isLoading = true;
  public requestError = false;
  public selectedGateway: IAvailableGateway | null = null;
  public isReloading = false;
  public gatewayTypesMap = GatewayTypesMap();

  public async created() {
    await this.loadData();
  }

  public reloadBrandGateways() {
    this.isReloading = true;
    this.loadData();
  }

  public isSelectedGateway({ id }: IAvailableGateway) {
    return !!this.component && this.selectedGateway?.id === id;
  }

  public renderInstructions({ instructions }: IAvailableGateway) {
    return {
      render: (h: CreateElement) =>
        h("p", { class: "has-text-grey has-white-space-preline" }, [
          instructions
        ])
    };
  }

  public setBrandGateway() {
    this.selectedGateway = _.find(this.availableGateways, [
      "brandGateway.gateway_id",
      this.preselectedId
    ]) as IAvailableGateway;
    if (!_.isEmpty(this.selectedGateway)) return;
    if (!this.autoSelectFirst || !this.availableGateways.length) return;

    this.selectedGateway =
      _.find(
        this.availableGateways,
        ({ brandGateway }) =>
          this.gatewayContext === GatewayContext.ADD &&
          !brandGateway.gateway.gateway_provider.store_on_payment &&
          [GatewayStoreType.ALWAYS, GatewayStoreType.EITHER].includes(
            brandGateway.gateway.gateway_provider.store_type
          )
      ) ?? this.availableGateways[0];
  }

  public getBrandGateways() {
    return this.$store.dispatch(`data/${this.dataModule}/list`, {
      brandId: this.brandId,
      ignoreStored: this.isReloading,
      params: {
        limit: 0,
        order: "order",
        "filter[active]": 1,
        with: "gateway.gateway_provider,gateway.card_types",
        ...this.currencyFilter
      },
      vm: this
    });
  }

  public async loadData() {
    try {
      await this.getBrandGateways();
      this.setBrandGateway();
      this.$emit("brand-gateways-loaded");
      this.requestError = false;
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public get currencyFilter() {
    return this.currencyId
      ? {
          "filter[gateway.currencies.id]": this.currencyId
        }
      : {};
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.dataModule,
      vm: this
    });
  }

  // Getters
  // Order is important here, lower the item higher the priority when filtering for availableGateways()
  public get gatewayTypes(): IGatewayType[] {
    return [
      {
        type: GatewayTypes.CARD,
        selector: (bg: IBrandGateway) => bg.gateway.type === GatewayTypes.CARD,
        label: this.gatewayTypesMap[GatewayTypes.CARD].label,
        component: CardGateway,
        isRenderless: false
      },
      {
        type: GatewayTypes.BANK_TRANSFER,
        selector: (bg: IBrandGateway) =>
          bg.gateway.type === GatewayTypes.BANK_TRANSFER,
        label: this.gatewayTypesMap[GatewayTypes.BANK_TRANSFER].label,
        component: BankTransferGateway,
        isRenderless: true
      },
      {
        type: GatewayTypes.DIRECT_DEBIT,
        selector: (bg: IBrandGateway) =>
          bg.gateway.type === GatewayTypes.DIRECT_DEBIT,
        label: this.gatewayTypesMap[GatewayTypes.DIRECT_DEBIT].label,
        component: DirectDebitGateway,
        isRenderless: true
      },
      {
        type: GatewayTypes.SEPA,
        selector: (bg: IBrandGateway) => bg.gateway.type === GatewayTypes.SEPA,
        label: this.gatewayTypesMap[GatewayTypes.SEPA].label,
        component: DirectDebitGateway,
        isRenderless: true
      },
      {
        type: GatewayTypes.MOBILE,
        selector: (bg: IBrandGateway) =>
          bg.gateway.type === GatewayTypes.MOBILE,
        label: this.gatewayTypesMap[GatewayTypes.MOBILE].label,
        component: MobileGateway,
        isRenderless: false
      },
      {
        type: GatewayTypes.OFFLINE,
        selector: (bg: IBrandGateway) =>
          bg.gateway.type === GatewayTypes.OFFLINE,
        label: this.gatewayTypesMap[GatewayTypes.OFFLINE].label,
        component: OfflinePaymentGateway,
        isRenderless: true
      },
      {
        /**
         * It's important we don't use bg.gateway.type in the selector as external store can be any type
         */
        selector: (bg: IBrandGateway) =>
          bg.gateway?.gateway_provider.external_store,
        label: this.$t("_.external_store"),
        props: {
          externalAuthReturnLocation: () => this.externalAuthReturnLocation
        },
        component: ExternalStoreGateway,
        isRenderless: false
      }
    ];
  }

  public get isGateway() {
    return [
      PaymentMethodType.GATEWAY_CARD,
      PaymentMethodType.GATEWAY_BANK_TRANSFER,
      PaymentMethodType.GATEWAY_DIRECT_DEBIT,
      PaymentMethodType.GATEWAY_MOBILE,
      PaymentMethodType.GATEWAY_OFFLINE,
      PaymentMethodType.EXTERNAL_STORE
    ].includes(this.value?.type as PaymentMethodType);
  }

  @Watch("value", { deep: true })
  public onValueChange() {
    if (!this.isGateway && !_.isEmpty(this.value?.type)) {
      this.selectedGateway = null;
    }
  }

  @Watch("selectedGateway", { deep: true })
  public onSelectedGatewayChange() {
    if (this.isGateway && !this.selectedGateway) {
      this.$emit("input", null);
    }
  }

  public get component() {
    return _.get(this.selectedGateway, "component");
  }

  public get activeGateways() {
    return _.filter(this.gateways, gateway => !!gateway.active);
  }

  public get hasActiveGateways() {
    return !!this.activeGateways.length;
  }

  public get activeStoredGateways() {
    return _.filter(this.activeGateways, ({ gateway }) => {
      return [GatewayStoreType.EITHER, GatewayStoreType.ALWAYS].includes(
        gateway.gateway_provider.store_type
      );
    });
  }

  public get hasActiveStoredGateways() {
    return !!this.activeStoredGateways.length;
  }

  public get availableGateways(): IAvailableGateway[] {
    // Filter out gateway store types
    let gateways = _.filter(this.gateways, g =>
      this.excludedGatewayStoreTypes.length
        ? !this.excludedGatewayStoreTypes.includes(
            g?.gateway?.gateway_provider?.store_type
          )
        : true
    );

    gateways = _.filter(gateways, (g: IBrandGateway) => {
      if (this.onlineOnly)
        return OnlineGatewayTypes.includes(_.get(g, "gateway.type"));
      return true;
    });

    const availableGateways = _.map(gateways, gateway => {
      return {
        ..._.last(this.gatewayTypes.filter(type => type.selector(gateway))),
        brandGateway: gateway,
        id: gateway.id,
        instructions:
          _.get(gateway, "gateway.payment_instructions_translated") ||
          _.get(gateway, "gateway.payment_instructions") ||
          ""
      };
    });

    return _.orderBy(availableGateways, [
      availableMethod => availableMethod.brandGateway.order
    ]) as IAvailableGateway[];
  }

  public selectGateway(gateway: IAvailableGateway) {
    this.selectedGateway = gateway;
  }

  public isPayPal(availableGateway: IAvailableGateway) {
    return _.get(
      availableGateway,
      "brandGateway.gateway.gateway_provider.code",
      ""
    )
      .toLowerCase()
      .includes("paypal");
  }
}
